import { cloneElement, isValidElement } from 'react';

import { css, styled, Tab as MuiTab, tabClasses as muiTabClasses } from '@mui/material';

import { InfoButton } from '../InfoButton';
import { tabClasses } from './theme';

import type { MouseEvent, ReactNode } from 'react';
import type { InfoButtonProps } from '../InfoButton';
import type { TabOwnerState, TabProps } from './types';

const isTextAdornment = (value: ReactNode): value is number | string => {
  return typeof value === 'number' || typeof value === 'string';
};

const isRenderableAdornment = (value: ReactNode) => {
  return value != null && value !== false;
};

export default function Tab(props: TabProps) {
  // 마이그레이션된 패턴: useThemeProps 제거, 기본값을 컴포넌트에서 직접 처리
  const {
    count,
    endAdornment,
    disabled,
    href,
    label,
    info,
    onClick: handleClick,
    value,
    LinkComponent,
    size = 'lg', // theme defaultProps에서 이동
    ...others
  } = props;

  const ownerState: TabOwnerState = {
    ...props,
    size, // 기본값이 적용된 변수를 ownerState에 재조합
  };
  const resolvedEndAdornment = endAdornment ?? count;
  const hasEndAdornment = isRenderableAdornment(resolvedEndAdornment);

  const content = (
    <>
      <TabItemLabel ownerState={ownerState}>{label}</TabItemLabel>
      {hasEndAdornment &&
        (isTextAdornment(resolvedEndAdornment) ? (
          <TabItemEndAdornment className={tabClasses.itemCount} ownerState={ownerState}>
            {resolvedEndAdornment}
          </TabItemEndAdornment>
        ) : (
          resolvedEndAdornment
        ))}
      {info && typeof info === 'string' ? (
        <TabItemInfoButton size="md" tooltipProps={{ message: info }} ownerState={ownerState} />
      ) : isValidElement<InfoButtonProps>(info) ? (
        cloneElement(info, { size: 'md', ...info.props })
      ) : (
        info
      )}
    </>
  );

  return (
    <TabItem
      disableFocusRipple
      disableRipple
      disableTouchRipple
      disabled={disabled}
      href={href}
      label={content}
      LinkComponent={LinkComponent}
      tabIndex={disabled ? undefined : 0}
      value={value}
      ownerState={ownerState}
      onClick={(e: MouseEvent<HTMLDivElement>) => handleClick?.(e, value)}
      {...others}
    />
  );
}

const TabItem = styled(MuiTab, {
  target: tabClasses.item, // 고정 클래스 - 항상 적용
})<{ ownerState: TabOwnerState; /** ButtonBase의 props 인터페이스 사용 */ href?: string }>(({
  ownerState,
  theme,
}) => {
  const { size } = ownerState;
  return css`
    padding: ${size === 'lg' ? '8px 16px' : '5.5px 16px'};
    flex-direction: row;
    gap: 4px;
    min-width: initial;
    max-width: initial;
    min-height: initial;
    position: relative;
    text-transform: initial;

    &:hover:not(.${muiTabClasses.selected}, .${muiTabClasses.disabled}) {
      background-color: ${theme.color['surface/hover']};
      &::before {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: 1px;
        border-bottom: ${theme.mixins.border('border/weak/default', 1)};
      }
      &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 1px;
        border-bottom: ${theme.mixins.border('border/strongest/default', 2)};
      }
    }

    &.${muiTabClasses.selected} {
      background-color: ${theme.color['actionSurface/tertiary/default']};
      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 100%;
        border-left: ${theme.mixins.border('border/weak/default', 1)};
        border-right: ${theme.mixins.border('border/weak/default', 1)};
      }
      &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 2px;
        background-color: ${theme.color['border/strongest/default']};
      }
    }

    ${theme.mixins.focusRing('inside')};
  `;
});

const TabItemLabel = styled('span', {
  target: tabClasses.itemLabel, // 고정 클래스 - 항상 적용
})<{ ownerState: TabOwnerState }>(({ ownerState, theme }) => [
  ownerState.size === 'md'
    ? theme.typography['label/semibold/md']
    : theme.typography['label/semibold/lg'],
  css`
    color: ${theme.color['text/weaker/default']};
    .${muiTabClasses.selected} & {
      color: ${theme.color['text/default']};
    }
    .${muiTabClasses.disabled} & {
      color: ${theme.color['text/disabled']};
    }
  `,
]);

const TabItemEndAdornment = styled('span', {
  target: tabClasses.itemEndAdornment, // 고정 클래스 - 항상 적용
})<{ ownerState: TabOwnerState }>(({ theme }) => [
  theme.typography['label/medium/lg'],
  css`
    display: inline-flex;
    align-items: center;
    color: ${theme.color['text/weakest/default']};
    .${muiTabClasses.disabled} & {
      color: ${theme.color['text/disabled']};
    }
  `,
]);

const TabItemInfoButton = styled(InfoButton, {
  target: tabClasses.itemInfoButton, // 고정 클래스 - 항상 적용
})<InfoButtonProps & { ownerState: TabOwnerState }>`
  .${tabClasses.item}:disabled & {
    pointer-events: initial;
  }
`;
